import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, FileText, ClipboardList, ShieldCheck,
  Settings, Bell, Search, ChevronRight, LogOut, Upload, Wind, Wrench, Package2, BookOpen,
} from 'lucide-react';
import { MobileSidebar } from './MobileSidebar';
import { DPassLogo } from './DPassLogo';
import { useAuth } from '../hooks/useAuth';

const IS_DEMO = typeof window !== 'undefined' && window.location.hostname === 'demo.d-pass.eu';

export function Layout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isLoading, isAuthenticated } = useAuth();

  const navigation = [
    { name: t('nav.dashboard'),     href: '/',              icon: LayoutDashboard },
    { name: t('nav.passports'),     href: '/passports',     icon: FileText },
    { name: t('nav.importCsv'),     href: '/import',        icon: Upload },
    { name: t('nav.auditLog'),      href: '/audit-log',     icon: ClipboardList },
    { name: t('nav.compliance'),    href: '/compliance',    icon: ShieldCheck },
    { name: t('nav.cbamCarbon'),    href: '/cbam',          icon: Wind },
    { name: t('nav.repairability'), href: '/repairability', icon: Wrench },
    { name: t('nav.packaging'),     href: '/packaging',     icon: Package2 },
    { name: t('nav.settings'),      href: '/settings',      icon: Settings },
    { name: t('nav.docs'),          href: '/docs',          icon: BookOpen },
  ];

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <DPassLogo className="w-10 h-10 animate-pulse" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const initials = (user?.first_name?.[0] ?? user?.email?.[0] ?? 'A').toUpperCase();

  return (
    <div className={`min-h-screen bg-slate-50 flex ${IS_DEMO ? 'pt-7' : ''}`}>
      {IS_DEMO && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-400 text-amber-900 text-xs font-semibold text-center py-1.5 px-4 flex items-center justify-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="flex-shrink-0">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          {t('layout.demo.warning')}
          <a href="https://dpp.d-pass.eu/login" className="underline hover:text-amber-950 ml-2" target="_blank" rel="noopener">
            {t('layout.demo.goToProduction')}
          </a>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 w-60 sidebar-gradient hidden lg:flex flex-col">
        {/* Brand */}
        <Link
          to="/"
          className="h-16 px-5 flex items-center gap-3 border-b border-white/5 hover:bg-white/5 transition-colors"
        >
          <DPassLogo className="w-8 h-8 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-white font-bold text-sm tracking-tight leading-none">D-Pass</p>
            <p className="text-slate-500 text-xs mt-0.5 leading-none">{t('layout.subtitle')}</p>
          </div>
        </Link>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
          <p className="px-3 mb-3 section-heading text-slate-600">{t('nav.platform')}</p>
          {navigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group ${
                  active
                    ? 'bg-brand-600/20 text-brand-400 shadow-sidebar-item'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon
                  className={`w-4 h-4 flex-shrink-0 transition-colors ${
                    active ? 'text-brand-400' : 'text-slate-500 group-hover:text-slate-300'
                  }`}
                />
                <span className="flex-1 truncate">{item.name}</span>
                {active && <ChevronRight className="w-3.5 h-3.5 text-brand-500/60 flex-shrink-0" />}
              </Link>
            );
          })}
        </nav>

        {/* Bottom status + user */}
        <div className="px-3 pb-5 space-y-2 border-t border-white/5 pt-4">
          <div className="px-3 py-2.5 rounded-xl bg-brand-600/10 border border-brand-600/20">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse flex-shrink-0" />
              <span className="text-brand-400 text-xs font-semibold">{t('layout.euCompliant')}</span>
            </div>
            <p className="text-slate-500 text-xs">{t('layout.gdpr')}</p>
          </div>

          {user && (
            <>
              <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors cursor-default">
                <div className="w-7 h-7 rounded-full bg-brand-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{initials}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">
                    {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.email}
                  </p>
                  <p className="text-slate-500 text-xs capitalize truncate">{user.role}</p>
                </div>
              </div>
              <button
                onClick={() => { logout(); navigate('/login'); }}
                className="flex items-center gap-2.5 px-3 py-2 w-full rounded-xl text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-all text-sm"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.signOut')}
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <MobileSidebar />
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder={t('layout.searchPlaceholder')}
                className="pl-9 pr-4 py-2 w-64 rounded-xl border border-slate-200 text-sm bg-slate-50 text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:bg-white transition-all"
              />
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              className="relative p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
              aria-label={t('layout.notifications')}
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-white" />
            </button>
            {user && (
              <div className="ml-1 pl-2 border-l border-slate-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{initials}</span>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 p-5 lg:p-8 animate-fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
